import axios from "axios";

// Detectar si estamos en desarrollo o producción
const isDevelopment = process.env.NODE_ENV === 'development';

// En desarrollo se usa el proxy, en producción la URL directa
const API_EXTERNAL = isDevelopment 
  ? '/api-proxy'  // Ruta del proxy local
  : process.env.VUE_APP_API_EXTERNAL; // URL real de la API externa

  // URL para pagos (diferente a la API principal)
const PAYMENTS_URL = isDevelopment 
? '/payments-proxy'  // Ruta del proxy local para pagos
: process.env.VUE_APP_PAYMENTS_URL; // URL real de la API de pagos

// Cliente para pagos
const paymentsClient = axios.create({
baseURL: PAYMENTS_URL,
headers: {
  "Content-Type": "application/json",
  "Accept": "application/json"
},
timeout: 10000,
withCredentials: false,
crossDomain: true,
});

// Cliente para autenticación
const authClient = axios.create({
  baseURL: `${API_EXTERNAL}/siaapi`,
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
  timeout: 10000,
});

// Cliente principal para las APIs
const apiClient = axios.create({
  baseURL: `${API_EXTERNAL}/siaapi/api/v1`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});
/*
// Cliente público sin autenticación
const publicApiClient = axios.create({
  baseURL: `${API_EXTERNAL}/siaapi/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});
*/
// Cliente para prospectos
const prospectsClient = axios.create({
  baseURL: `${API_EXTERNAL}/SiaApi/api`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

// Listados de estados, ciudades, colonias, etc
const apiListados = axios.create({
  baseURL: `${API_EXTERNAL}/siaapi/api`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

const CURPClient = axios.create({
  baseURL: `${API_EXTERNAL}/siaapi/api`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

// Almacenar el token
let authToken = null;

// Obtener el token de autenticación
export const getAuthToken = async () => {
  try {
    const response = await authClient.post("/token", new URLSearchParams({
      apikey: process.env.VUE_APP_API_KEY, // Usa la variable de entorno
      grant_type: "apikey"
    }));
    
    authToken = response.data.access_token; // Ajusta según la estructura de respuesta
    return authToken;
  } catch (error) {
    console.error("Error obteniendo token:", error);
    throw error;
  }
};

// Agregar automáticamente el Bearer Token al cliente principal
apiClient.interceptors.request.use(
  async (config) => {
    // Si no hay token, obtenerlo
    if (!authToken) {
      await getAuthToken();
    }
    
    // Agregar el Bearer Token al header
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Manejo de errores de autenticación del cliente principal
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Intentar renovar el token
    if (error.response?.status === 401) {
      try {
        authToken = null; // Limpiar token actual
        await getAuthToken(); // Obtener nuevo token
        
        // Reintentar con el nuevo token
        const originalRequest = error.config;
        originalRequest.headers.Authorization = `Bearer ${authToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error("Error renovando token:", refreshError);
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Agregar automáticamente el Bearer Token al cliente CURP
CURPClient.interceptors.request.use(
  async (config) => {
    // Si no hay token, obtenerlo
    if (!authToken) {
      await getAuthToken();
    }
    
    // Agregar el Bearer Token al header
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Funciones para cada endpoint del cliente principal
export const getPeriods = (params) => apiClient.get("/students/Periods", { params });
export const getSchedule = (params) => apiClient.get("/students/Schedule", { params });
export const getGrades = (params) => apiClient.get("/students/Grades", { params });
export const getCalendarioColegiatura = (params) => apiClient.get("/students/Charges", { params });
export const getTickets = (params) => apiClient.get("/students/Tickets", { params });
export const getCalendarioConvenio = (params) => apiClient.get("/students/AgreementCharges", { params });
export const getAgreementTickets = (params) => apiClient.get("/students/AgreementTickets", { params });
export const getIncidents = (params) => apiClient.get("/students/Incidents", { params });
export const getDocuments = (params) => apiClient.get("/students/Documents", { params });
export const getPartials = (params) => apiClient.get("/students/Partials", { params });
export const submitDisagreement = (data) => apiClient.post("/students/Disagreement", data);
export const getDisagreements = (params) => apiClient.get("/students/Disagreement", { params });
export const getBenefits = (params) => apiClient.get("/students/benefits", { params });
export const getConcepts = (params) => apiClient.get("/students/concepts", { params });
export const getCareerFiles = (params) => apiClient.get("/students/careerfiles", { params });
export const getTicketsBilling = (params) => apiClient.get("/students/ticketsbilling", { params });
export const getLegalFiles = (params) => apiClient.get("/students/legalfiles", { params });
export const getActivities = (params) => apiClient.get("/students/activities", { params });
export const uploadActivity = (params) => apiClient.post('/students/activity', params);
export const getScheduleDocument = (params) => apiClient.get("/students/ScheduleDocument", { params, responseType: 'blob' });

//CURP
export const getCURP = (params) => CURPClient.get("/Curp", { params, timeout: 30000 });

//Alertas
export const getPendingNotices = () => { return axios.get('/notices/pending'); };
export const acknowledgeNotice = (noticeId, action) => { return axios.post(`/notices/${noticeId}/acknowledge`, { action }); };

//Perfil sin foto
export const getStudentData = (idstudent) => apiClient.get(`/students/Student?idstudent=${idstudent}&source=profile`);
export const updateStudentData = (idstudent, data) => apiClient.put(`/students/Student?idstudent=${idstudent}`, data);
export const getStudentPhoto = (idstudent) => apiClient.get(`/students/Student?idstudent=${idstudent}`);
//FACTURACION
export const getBillingData = (idstudent) => apiClient.get(`/students/billing?idstudent=${idstudent}`);

export const createBillingData = (data) => apiClient.post('/students/billing', data);

export const updateBillingData = (idstudent, data) => apiClient.put(`/students/billing?idstudent=${idstudent}`, data);

export const createInvoice = (data) => apiClient.post('/CrolERP/NotaVentaFactura', data);

//Constancia
export const getConstancia = (params) => apiClient.get("/students/documentstatus", { params });

//Credencial
export const getCredencial = (params) => apiClient.get("/services/info", { params });

// Funciones de verificación de email de facturación
// Verificar correo electrónico
export const verifyEmail = async (email) => {
  const response = await axios.post('/verify-email', { email });
  return response.data;
};

// Validar código de verificación
export const validateVerificationCode = async (email, code) => {
  const response = await axios.post('/validate-code', { email, code });
  return response.data;
};

// Verificar estado de verificación de email
export const checkEmailVerificationStatus = async (email) => {
  const response = await axios.post('/check-email-verification', { email });
  return response.data;
};
// FIN PARA DATOS DE FACTURACION



// Función para obtener prospectos (usa cliente específico)
export const getProspects = (params) => prospectsClient.get('/Prospects', { params });

// Crear filtros
export const createProspectsFilter = ({
  pName = "",
  pStatusId = "",
  pPromotorName = "",
  pStartDate = "1900-12-12",
  pEndDate = "2090-12-12",
  pUserName = ""
} = {}) => {
  return {
    _dc: '',
    page: 1,
    start: 0,
    limit: 50,
    filter: JSON.stringify([
      {"property":"pSource","value":"GET_Prospects"},
      {"property":"pName","value": pName},
      {"property":"pStatusId","value": pStatusId},
      {"property":"pPromotorName","value": pPromotorName},
      {"property":"pStartDate","value": pStartDate},
      {"property":"pEndDate","value": pEndDate},
      {"property":"pUserName","value": pUserName}
    ])
  };
};

// FUNCIONES PARA LISTADOS
export const getStates = (countryId = 1) => apiListados.get('/States', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    start: 0,
    filter: JSON.stringify([
      { "property": "pSource", "value": "GET_States" },
      { "property": "pCountryId", "value": countryId }
    ])
  }
});

export const getCities = (stateId) => apiListados.get('/Cities', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    start: 0,
    filter: JSON.stringify([{ "property": "pStateId", "value": stateId }])
  }
});

export const getColonies = (cityId) => apiListados.get('/Colonies', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    start: 0,
    filter: JSON.stringify([{ "property": "pCityId", "value": cityId }])
  }
});

export const getSchools = () => prospectsClient.get('/Schools', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([{"property":"pSource","value":"GetSchoolsCombo"}])
  }
});

export const getWorks = () => prospectsClient.get('/Works', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([{"property":"pSource","value":"GetCmbWorks"}])
  }
});

export const getCountries = () => prospectsClient.get('/Countries', {
  params: {
    _dc: new Date().getTime(),
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([{"property":"pSource","value":"GetCmbCountry"}])
  }
});

export const getInstitutes = () => prospectsClient.get('/Institutes', {
  params: {
    _dc: new Date().getTime(),
    page: 1,
    start: 0,
    limit: 25
  }
});

export const getInstitutesByStudent = (params) => prospectsClient.get('/Institutes/InstituteByStudent', {
  params: {
    idstudent: params,
    _dc: new Date().getTime(),
    page: 1,
    start: 0,
    limit: 25
  }
});

export const getCampus = (instituteId) => prospectsClient.get('/Campus', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([
      {"property":"Source","value":"CmbGetCampus"},
      {"property":"pInstituteId","value": instituteId}
    ])
  }
});

export const getProspectPeriods = (campusId) => prospectsClient.get('/CbPT', {
  params: {
    _dc: new Date().getTime(),
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([
      {"property":"pSource","value":"Periods"},
      {"property":"pCampus","value": campusId}
    ])
  }
});

export const getCareers = (campusId, periodId) => prospectsClient.get('/CbPT', {
  params: {
    _dc: new Date().getTime(),
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([
      {"property":"pSource","value":"Careers"},
      {"property":"pGrade","value":1},
      {"property":"pPeriod","value": periodId},
      {"property":"pCampus","value": campusId},
      {"property":"pSourceType","value":1}
    ])
  }
});

// Función para obtener turnos
export const getShifts = (campusId, periodId, careerId) => prospectsClient.get('/CbPT', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([
      {"property":"pSource","value":"Shifts"},
      {"property":"pGrade","value":1},
      {"property":"pCareer","value": careerId},
      {"property":"pPeriod","value": periodId},
      {"property":"pCampus","value": campusId},
      {"property":"pSourceType","value":1}
    ])
  }
});

// FUNCIONES PARA SEGUIMIENTOS
export const createProspect = (data) => prospectsClient.post('/Prospects/prospects', data);

// Función para crear periodo del prospecto
export const createProspectPeriod = (data) => prospectsClient.post('/Prospects_Periods/prospectPeriods', data);

// Obtener tipos de contacto para seguimientos
export const getContactTypes = () => apiListados.get('/ContactTypes', {
  params: {
    _dc: new Date().getTime(),
    query: '',
    page: 1,
    start: 0,
    limit: 25,
    filter: JSON.stringify([{ "property": "pSource", "value": "GET_CmbContactTypes" }])
  }
});

// Crear un nuevo seguimiento/actividad de prospecto
export const createProspectActivity = (data) => apiListados.post('/Prospects_Activities/prospectsActivities', data);

// Obtener seguimientos/actividades de un prospecto
export const getProspectActivities = (prospectId, memberId) => apiListados.get('/Prospects_Activities', {
  params: {
    _dc: new Date().getTime(),
    page: 1,
    start: 0,
    limit: 100,
    filter: JSON.stringify([
      {"property":"pSource","value":"GetActivitiesXProspect"},
      {"property":"pStartDate","value":"1800-12-30"},
      {"property":"pEndDate","value":"2999-12-12"},
      {"property":"pProspectId","value": prospectId},
      {"property":"pMemberId","value": memberId}
    ])
  }
});

// Obtener información de prospectos con filtros específicos para seguimientos
export const getProspectsForFollowUp = (userName) => apiListados.get('/Prospects', {
  params: {
    _dc: new Date().getTime(),
    page: 1,
    start: 0,
    limit: 100,
    filter: JSON.stringify([
      {"property":"pSource","value":"GET_Prospects"},
      {"property":"pName","value":""},
      {"property":"pStatusId","value":""},
      {"property":"pPromotorName","value":""},
      {"property":"pStartDate","value":"1900-12-12"},
      {"property":"pEndDate","value":"2090-12-12"},
      {"property":"pUserName","value": userName}
    ])
  }
});

// FUNCIONES PARA PAGOS
export const processPayment = (type, order) => paymentsClient.post(`/api/${type}`, order);

// Para logout
export const clearAuthToken = () => {
  authToken = null;
};